import React, { useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Alert from "@material-ui/lab/Alert";

import AddIcon from "@material-ui/icons/Add";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";

import {
  createPaymentAccount,
  clearErrorCreate,
} from "../../redux/features/hotels/paymentAccountsSlice";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  emailRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  emailInput: {
    flex: 1,
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    minHeight: 32,
  },
  errorAlert: {
    marginTop: theme.spacing(1),
  },
}));

const LinkStripeAccountDialog = ({
  open,
  onClose,
  hotelId,
  condoId,
  onSuccess,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isCreating, errorCreate } = useSelector((s) => s.paymentAccounts);

  const [stripeAccountId, setStripeAccountId] = useState("");
  const [stripeAccountName, setStripeAccountName] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);

  const resetForm = () => {
    setStripeAccountId("");
    setStripeAccountName("");
    setEmailInput("");
    setEmails([]);
    dispatch(clearErrorCreate());
  };

  const handleClose = () => {
    if (isCreating) return;
    resetForm();
    onClose();
  };

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (trimmed && !emails.includes(trimmed)) {
      setEmails((prev) => [...prev, trimmed]);
    }
    setEmailInput("");
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleSubmit = async () => {
    dispatch(clearErrorCreate());
    const payload = {
      stripe_account_id: stripeAccountId.trim(),
      stripe_account_name: stripeAccountName.trim(),
      emails,
      hotel_id: hotelId ? Number(hotelId) : null,
      condo_id: condoId ? Number(condoId) : null,
    };
    const result = await dispatch(createPaymentAccount(payload));
    if (!result.error) {
      resetForm();
      onSuccess && onSuccess(result.data);
    }
  };

  const isValid =
    stripeAccountId.trim() && stripeAccountName.trim() && emails.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        disableTypography
        style={{ display: "flex", alignItems: "center", gap: 8 }}
      >
        <AccountBalanceIcon color="primary" />
        <Typography variant="h6" style={{ flex: 1 }}>
          Link Stripe Account
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          className={classes.field}
          label="Stripe Account ID"
          variant="outlined"
          size="small"
          fullWidth
          required
          value={stripeAccountId}
          onChange={(e) => setStripeAccountId(e.target.value)}
          disabled={isCreating}
          placeholder="acct_1ABCxxx..."
          helperText="The Stripe Connect account ID (starts with acct_)"
        />
        <TextField
          className={classes.field}
          label="Account Display Name"
          variant="outlined"
          size="small"
          fullWidth
          required
          value={stripeAccountName}
          onChange={(e) => setStripeAccountName(e.target.value)}
          disabled={isCreating}
          placeholder="e.g. Grand Coral Beach Hotel"
        />

        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          gutterBottom
        >
          Notification emails — press Enter or comma to add (At least one
          notification email is required.)
        </Typography>
        <div className={classes.emailRow}>
          <TextField
            className={classes.emailInput}
            label="Add email"
            variant="outlined"
            size="small"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={handleEmailKeyDown}
            disabled={isCreating}
            type="email"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addEmail}
            disabled={isCreating || !emailInput.trim()}
          >
            Add
          </Button>
        </div>
        <div className={classes.chipsContainer}>
          {emails.map((email) => (
            <Chip
              key={email}
              label={email}
              size="small"
              onDelete={() =>
                setEmails((prev) => prev.filter((e) => e !== email))
              }
              disabled={isCreating}
            />
          ))}
        </div>

        {errorCreate && (
          <Alert severity="error" className={classes.errorAlert}>
            {errorCreate.errorMessage ||
              "Failed to link account. Please try again."}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isCreating || !isValid}
          startIcon={
            isCreating ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {isCreating ? "Linking…" : "Link Account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkStripeAccountDialog;
